/**
 * Router configuration — React Router v6 with layout routes, lazy loading & error boundaries.
 */
import React, { Suspense } from 'react';
import { createHashRouter, Navigate } from 'react-router-dom';
import { RequireAuth, GuestOnly, RequireRole } from './guards';
import { ROUTES } from './routes';
import PageLoader from '@/components/common/PageLoader';
import RouteErrorBoundary from '@/components/common/RouteErrorBoundary';

// Helper for React.lazy with automatic chunk retry on deployment asset hash updates
const lazyWithRetry = (importFn: () => Promise<any>) =>
  React.lazy(async () => {
    try {
      const pageModule = await importFn();
      // Reset retry flag after clean module load
      window.sessionStorage.removeItem('ep_chunk_retry');
      
      const Component = pageModule?.default?.default || pageModule?.default || pageModule;
      return { default: Component };
    } catch (error) {
      const hasRetried = window.sessionStorage.getItem('ep_chunk_retry');
      if (!hasRetried) {
        window.sessionStorage.setItem('ep_chunk_retry', 'true');
        window.location.reload();
        return new Promise(() => {});
      }
      window.sessionStorage.removeItem('ep_chunk_retry');
      throw error;
    }
  });

// Helper to wrap lazy components in Suspense
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Auth pages
const LoginPage = lazyWithRetry(() => import('@/features/auth/LoginPage'));
const SignupPage = lazyWithRetry(() => import('@/features/auth/SignupPage'));
const RecoverPage = lazyWithRetry(() => import('@/features/auth/RecoverPage'));

// Layout (static imports for shell infrastructure)
import AppShell from '@/components/layout/AppShell';
import AdminLayout from '@/features/admin/layout/AdminLayout';

// Dashboard & Admin pages
const DashboardPage = lazyWithRetry(() => import('@/features/dashboard/DashboardPage'));
const UsersPage = lazyWithRetry(() => import('@/features/admin/pages/UsersPage'));
const TeacherStatsPage = lazyWithRetry(() => import('@/features/admin/pages/TeacherStatsPage'));
const TeacherDetailPage = lazyWithRetry(() => import('@/features/admin/pages/TeacherDetailPage'));
const StudentStatsPage = lazyWithRetry(() => import('@/features/admin/pages/StudentStatsPage'));
const AdminStatsGroupsPage = lazyWithRetry(() => import('@/features/admin/pages/AdminStatsGroupsPage'));
const AdminTrainersPage = lazyWithRetry(() => import('@/features/admin/pages/AdminTrainersPage'));
const AdminTrainersModerationPage = lazyWithRetry(() => import('@/features/admin/pages/AdminTrainersModerationPage'));
const MaterialsAdminPage = lazyWithRetry(() => import('@/features/admin/pages/MaterialsAdminPage'));
const PaymentSystemsPage = lazyWithRetry(() => import('@/features/admin/PaymentSystemsPage'));
const PlatformSettingsPage = lazyWithRetry(() => import('@/features/admin/pages/PlatformSettingsPage'));
const AdminTagsPage = lazyWithRetry(() => import('@/features/admin/pages/AdminTagsPage'));
const AdminTtsPage = lazyWithRetry(() => import('@/features/admin/pages/AdminTtsPage'));
const AdminMarketPage = lazyWithRetry(() => import('@/features/admin/pages/AdminMarketPage'));
const AdminPricingPage = lazyWithRetry(() => import('@/features/admin/pages/AdminPricingPage'));
const AdminSongsPage = lazyWithRetry(() => import('@/features/admin/pages/AdminSongsPage'));
const AdminCertificatesPage = lazyWithRetry(() => import('@/features/admin/pages/AdminCertificatesPage'));
const BugReportsAdminPage = lazyWithRetry(() => import('@/features/admin/BugReportsAdminPage'));

// Songs pages
const SongsCatalogPage = lazyWithRetry(() => import('@/features/songs/SongsCatalogPage'));
const SongPlayerPage = lazyWithRetry(() => import('@/features/songs/SongPlayerPage'));
const AlbumDetailPage = lazyWithRetry(() => import('@/features/songs/AlbumDetailPage'));

// Shared public stats & share pages
const SharedStatsPage = lazyWithRetry(() => import('@/features/admin/pages/SharedStatsPage'));
const ShareAppPage = lazyWithRetry(() => import('@/features/shared/pages/ShareAppPage'));

// Groups pages
const GroupsListPage = lazyWithRetry(() => import('@/features/groups/GroupsListPage'));
const GroupDetailPage = lazyWithRetry(() => import('@/features/groups/GroupDetailPage'));
const OnlineLessonPage = lazyWithRetry(() => import('@/features/groups/online/OnlineLessonPage'));

// Materials & Trainers pages
const MaterialsListPage = lazyWithRetry(() => import('@/features/materials/MaterialsListPage'));
const TrainersPage = lazyWithRetry(() => import('@/features/exercises/TrainersPage'));
const MyCoursesPage = lazyWithRetry(() => import('@/features/courses/MyCoursesPage'));
const MarketPage = lazyWithRetry(() => import('@/features/market/MarketPage'));
const CoursePage = lazyWithRetry(() => import('@/features/market/CoursePage'));
const SlideViewerPage = lazyWithRetry(() => import('@/features/market/SlideViewerPage'));

// Exercises pages
const ExercisesListPage = lazyWithRetry(() => import('@/features/exercises/ExercisesListPage'));
const ExercisePage = lazyWithRetry(() => import('@/features/exercises/ExercisePage'));
const CardsTrainerTestPage = lazyWithRetry(() => import('@/features/trainers/cards/CardsTrainerTestPage'));

// Dialogs
const DialogsListPage = lazyWithRetry(() => import('@/features/dialogs/DialogsListPage'));

// Tests / Questionnaire
const QuestionnaireListPage = lazyWithRetry(() => import('@/features/trainers/questionnaire/QuestionnaireListPage'));
const TestsListPage = lazyWithRetry(() => import('@/features/tests/TestsListPage'));

// Treadmills
const TreadmillsListPage = lazyWithRetry(() => import('@/features/trainers/treadmills/TreadmillsListPage'));

// Profile & Settings
const ProfilePage = lazyWithRetry(() => import('@/features/profile/ProfilePage'));
const TeacherSettingsPage = lazyWithRetry(() => import('@/features/profile/TeacherSettingsPage'));

// Vocabulary
const VocabularyPage = lazyWithRetry(() => import('@/features/vocabulary/VocabularyPage'));

// Schedule
const TeacherSchedulePage = lazyWithRetry(() => import('@/features/schedule/TeacherSchedulePage'));

// Posts
const PostsPage = lazyWithRetry(() => import('@/features/posts/PostsPage'));
// Notifications
const NotificationsPage = lazyWithRetry(() => import('@/features/notifications/NotificationsPage'));


export const router = createHashRouter([
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      /* ── Guest routes (login, signup, recover) ── */
      {
        element: <GuestOnly />,
        children: [
          { path: ROUTES.LOGIN, element: withSuspense(LoginPage) },
          { path: ROUTES.SIGNUP, element: withSuspense(SignupPage) },
          { path: ROUTES.RECOVER, element: withSuspense(RecoverPage) },
        ],
      },

      /* ── Public routes (no auth, no sidebar) ── */
      {
        path: ROUTES.SHARED_STATS,
        element: withSuspense(SharedStatsPage)
      },
      {
        path: ROUTES.SLIDE_VIEWER,
        element: withSuspense(SlideViewerPage)
      },
      {
        path: '/share/:type/:id',
        element: withSuspense(ShareAppPage)
      },

      /* ── Admin routes — requires 'admin' role, own layout ── */
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireRole roles={['admin', 'moderator']} />,
            children: [
              {
                element: <AdminLayout />,
                children: [
                  { path: ROUTES.ADMIN, element: <Navigate to={ROUTES.ADMIN_USERS} replace /> },
                  { path: ROUTES.ADMIN_USERS, element: withSuspense(UsersPage) },
                  { path: ROUTES.ADMIN_TEACHERS, element: withSuspense(TeacherStatsPage) },
                  { path: ROUTES.ADMIN_TEACHER_DETAIL, element: withSuspense(TeacherDetailPage) },
                  { path: ROUTES.ADMIN_STUDENTS, element: withSuspense(StudentStatsPage) },
                  { path: ROUTES.ADMIN_STATS_GROUPS, element: withSuspense(AdminStatsGroupsPage) },
                  { path: ROUTES.ADMIN_TRAINERS, element: withSuspense(AdminTrainersPage) },
                  { path: ROUTES.ADMIN_TRAINERS_MODERATION, element: withSuspense(AdminTrainersModerationPage) },
                  { path: ROUTES.ADMIN_MATERIALS, element: withSuspense(MaterialsAdminPage) },
                  { path: ROUTES.ADMIN_PAYMENTS, element: withSuspense(PaymentSystemsPage) },
                  { path: ROUTES.ADMIN_PLATFORM_SETTINGS, element: withSuspense(PlatformSettingsPage) },
                  { path: ROUTES.ADMIN_TAGS, element: withSuspense(AdminTagsPage) },
                  { path: ROUTES.ADMIN_TTS,  element: withSuspense(AdminTtsPage) },
                  { path: ROUTES.ADMIN_MARKET, element: withSuspense(AdminMarketPage) },
                  { path: ROUTES.ADMIN_PRICING, element: withSuspense(AdminPricingPage) },
                  { path: ROUTES.ADMIN_SONGS, element: withSuspense(AdminSongsPage) },
                  { path: ROUTES.ADMIN_CERTIFICATES, element: withSuspense(AdminCertificatesPage) },
                  { path: ROUTES.ADMIN_BUG_REPORTS, element: withSuspense(BugReportsAdminPage) },
                ],
              },
            ],
          },
        ],
      },

      /* ── Authenticated routes ── */
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: ROUTES.HOME, element: withSuspense(DashboardPage) },
              { path: ROUTES.GROUPS, element: withSuspense(GroupsListPage) },
              { path: ROUTES.GROUP_DETAIL, element: withSuspense(GroupDetailPage) },
              { path: ROUTES.MATERIALS, element: withSuspense(MaterialsListPage) },
              { path: ROUTES.MY_COURSES, element: withSuspense(MyCoursesPage) },
              { path: ROUTES.MARKET, element: withSuspense(MarketPage) },
              { path: ROUTES.COURSE_DETAIL, element: withSuspense(CoursePage) },
              { path: ROUTES.TRAINERS, element: withSuspense(TrainersPage) },
              { path: ROUTES.EXERCISES_LIST, element: withSuspense(ExercisesListPage) },
              { path: ROUTES.EXERCISES, element: withSuspense(ExercisePage) },
              { path: ROUTES.DIALOGS_LIST, element: withSuspense(DialogsListPage) },
              { path: ROUTES.TRAINERS_CARDS_TEST, element: withSuspense(CardsTrainerTestPage) },
              { path: ROUTES.TRAINERS_QUESTIONNAIRE, element: withSuspense(QuestionnaireListPage) },
              { path: ROUTES.TESTS_LIST, element: withSuspense(TestsListPage) },
              { path: ROUTES.TRAINERS_TREADMILLS, element: withSuspense(TreadmillsListPage) },
              { path: ROUTES.PROFILE, element: withSuspense(ProfilePage) },
              { path: ROUTES.SETTINGS, element: withSuspense(TeacherSettingsPage) },
              { path: ROUTES.VOCABULARY, element: withSuspense(VocabularyPage) },
              { path: ROUTES.SCHEDULE, element: withSuspense(TeacherSchedulePage) },
              { path: ROUTES.POSTS, element: withSuspense(PostsPage) },
              { path: ROUTES.NOTIFICATIONS, element: withSuspense(NotificationsPage) },
              { path: ROUTES.SONGS, element: withSuspense(SongsCatalogPage) },
              { path: ROUTES.SONG_DETAIL, element: withSuspense(SongPlayerPage) },
              { path: ROUTES.SONG_ALBUM_DETAIL, element: withSuspense(AlbumDetailPage) },
            ],
          },
          /* ── Full-screen routes (no AppShell) ── */
          { path: ROUTES.ONLINE_LESSON, element: withSuspense(OnlineLessonPage) },
        ],
      },

      /* ── Catch-all ── */
      { path: '*', element: <Navigate to={ROUTES.HOME} replace /> },
    ]
  }
], { future: { v7_startTransition: true } as any });
